import { useEffect, useRef, useState } from 'react';
import { isThemePreview } from '../api/previewVisit';
import { isThemeEditor } from '../editor/editorVisit';
import { trackPlaced } from './commerceEvents';
import { cardReturnOf, confirmCardPayment, heldCardOrder, holdCardOrder, releaseCardOrder, retryCardPayment } from './cardPayment';
import { useCommerce } from './ShopCommerce';

// The thank-you page's half of a card payment: back from Stripe, it asks the shop whether the session paid its
// order. Paid, the order is confirmed exactly as a cash order is, Purchase and all; not paid, the shopper is told
// so and offered Stripe's page again. A visit that did not come from Stripe is left alone.

/** Where a return from Stripe stands. */
export type CardReturnState =
  | { stage: 'none' }
  | { stage: 'checking' }
  | { stage: 'unpaid'; orderNumber: string; retry: () => void; retrying: boolean }
  | { stage: 'failed' };

// Reads a return from Stripe on this visit and settles it, once.
export function useCardReturn(): CardReturnState {
  const commerce = useCommerce();
  const [state, setState] = useState<CardReturnState>({ stage: 'none' });
  const settled = useRef(false);
  useEffect(() => {
    if (settled.current || isThemeEditor() || isThemePreview()) return;
    const back = cardReturnOf(window.location.search);
    const held = heldCardOrder();
    if (!held || (!back.sessionId && !back.cancelled)) return;
    settled.current = true;
    const sessionId = back.sessionId ?? held.sessionId;
    // Sends the shopper to Stripe's page again with a fresh session, kept like the first.
    const retry = () => {
      setState((now) => (now.stage === 'unpaid' ? { ...now, retrying: true } : now));
      retryCardPayment(sessionId)
        .then((page) => { holdCardOrder({ ...held, sessionId: page.sessionId }); window.location.assign(page.url); })
        .catch(() => setState({ stage: 'failed' }));
    };
    setState({ stage: 'checking' });
    confirmCardPayment(sessionId).then((result) => {
      if (!result.paid) return setState({ stage: 'unpaid', orderNumber: result.orderNumber, retry, retrying: false });
      releaseCardOrder();
      trackPlaced(held.placed, held.contents, held.currency);
      commerce?.complete(held.placed);
      setState({ stage: 'none' });
    }).catch(() => setState({ stage: 'failed' }));
  }, [commerce]);
  return state;
}
